import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../../config/api";
import bannerImg from "../../../Images/image 305.svg";
import Rectangle_shade from "../../../Images/Rectangle_shade.svg";
import defaultThumbnail from "../../../Images/courses-v1.svg";
import { MapPin } from "lucide-react";

export default function CoursesPage() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const coursesPerPage = 12;

  // ===============================
  // Date Formatter
  // ===============================
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // ===============================
  // Program Duration
  // ===============================
  const getProgramDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return "N/A";

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end - start;

    if (diffTime < 0) return "N/A";

    const diffDays =
      Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    if (diffDays <= 30) return `${diffDays} days`;

    const diffMonths = Math.ceil(diffDays / 30);
    return `${diffMonths} month${diffMonths > 1 ? "s" : ""}`;
  };

  // ===============================
  // Discount Logic
  // ===============================
  const calculateDiscountedPrice = (schedule, originalPrice) => {
    if (
      !schedule ||
      !schedule.discount_applicable ||
      !schedule.discount_value
    ) {
      return Number(originalPrice) || 0;
    }

    const price = Number(originalPrice) || 0;
    const discountValue = Number(schedule.discount_value) || 0;

    const now = new Date();
    const validFrom = schedule.discount_valid_from
      ? new Date(schedule.discount_valid_from)
      : null;
    const validTo = schedule.discount_valid_to
      ? new Date(schedule.discount_valid_to)
      : null;

    if (validFrom && now < validFrom) return price;
    if (validTo && now > validTo) return price;

    if (schedule.discount_type === "Percentage") {
      return price - (price * discountValue) / 100;
    }

    if (schedule.discount_type === "FlatAmount") {
      return Math.max(0, price - discountValue);
    }

    return price;
  };

  // ===============================
  // Navigation (PASS SCHEDULE ID)
  // ===============================
  const handleSubmit = (courseId, scheduleId) => {
    navigate(`/courses/${courseId}`, {
      state: { scheduleId },
    });
  };

  // ===============================
  // Fetch Courses
  // ===============================
  const fetchCourses = async (page, append = false) => {
    try {
      setLoading(true);
      const min = (page - 1) * coursesPerPage;
      const max = page * coursesPerPage;

      const response = await axios.get(`${API_URL}/courses/`, {
        params: { min, max },
      });

      const data = response.data?.data;

      if (!Array.isArray(data)) {
        console.error("Unexpected data format:", response.data);
        return;
      }

      const formattedCourses = [];

      data.forEach((course) => {
        const schedules = course.schedules || [];

        // Create ONE CARD PER SCHEDULE
        schedules.forEach((schedule) => {
          const originalPrice = Number(course.amount) || 0;
          const discountedPrice = calculateDiscountedPrice(
            schedule,
            originalPrice
          );

          formattedCourses.push({
            courseId: course.course_id,
            scheduleId: schedule.schedule_id,
            title: course.title,
            instructor:
              course.instructors?.[0]?.user?.name ||
              "Unknown Instructor",
            originalPrice,
            discountedPrice,
            hasDiscount:
              schedule.discount_applicable &&
              discountedPrice < originalPrice,
            lessons: `${course.total_lessons} Lessons`,
            modules: `${course.total_modules} Modules`,
            days: getProgramDuration(
              schedule.start_date,
              schedule.end_date
            ),
            date: formatDate(schedule.start_date),
            location: schedule.location || "Online",
            thumbnail:
              course.program_thumbnail || defaultThumbnail,
          });
        });
      });

      if (append) {
        setCourses((prev) => [...prev, ...formattedCourses]);
      } else {
        setCourses(formattedCourses);
      }

      setHasMore(formattedCourses.length === coursesPerPage);
    } catch (err) {
      console.error("Error fetching courses:", err);
    } finally {
      setLoading(false);
    }
  };

  // ===============================
  // Load More
  // ===============================
  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchCourses(nextPage, true);
  };

  // ===============================
  // Initial Load
  // ===============================
  useEffect(() => {
    fetchCourses(1);
  }, []);

  return (
    <section className="w-full">
      {/* Banner */}
      <div className="relative w-full">
        <img
          src={bannerImg}
          alt="Banner"
          className="w-full h-[350px] md:h-[450px] object-cover"
        />
        <img
          src={Rectangle_shade}
          alt="Shade"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute left-6 sm:left-12 lg:left-20 top-1/3">
          <h2 className="text-3xl md:text-5xl font-bold text-zinc-800">
            Our Programs
          </h2>
        </div>
      </div>

      {/* Courses Grid */}
      <div className="container mx-auto px-6 sm:px-12 py-16">
        {courses.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {courses.map((course, index) => (
              <div
                key={`${course.courseId}-${course.scheduleId}-${index}`}
                className="bg-white rounded-2xl shadow-md hover:shadow-xl transition overflow-hidden flex flex-col"
              >
                {/* Image */}
                <img
                  src={course.thumbnail}
                  alt={course.title}
                  className="w-full h-44 object-cover rounded-t-2xl"
                />

                {/* Body */}
                <div className="p-4 flex flex-col flex-grow">
                  {/* LOCATION — BELOW IMAGE */}
                  <div className="flex items-center gap-1 text-[11px] text-emerald-500 mb-1">
                    <MapPin size={12} />
                    <span className="font-medium">
                      {course.location}
                    </span>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-800">
                    {course.title}
                  </h3>

                  <p className="text-sm text-gray-500 mb-2">
                    {course.instructor}
                  </p>

                  {/* Price */}
                  <div className="mb-3">
                    {course.hasDiscount ? (
                      <>
                        <span className="line-through text-red-400 text-sm mr-2">
                          ${course.originalPrice.toFixed(2)}
                        </span>
                        <span className="text-green-600 font-bold text-lg">
                          ${course.discountedPrice.toFixed(2)}
                        </span>
                      </>
                    ) : (
                      <span className="text-blue-600 font-bold text-lg">
                        ${course.originalPrice.toFixed(2)}
                      </span>
                    )}
                  </div>

                  {/* Meta */}
                  <div className="text-xs text-gray-400 mt-auto">
                    {course.modules} • {course.days} • Start:{" "}
                    {course.date}
                  </div>

                  <button
                    onClick={() =>
                      handleSubmit(
                        course.courseId,
                        course.scheduleId
                      )
                    }
                    className="mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2 rounded-lg transition"
                  >
                    Enroll Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500">
            {loading
              ? "Loading courses..."
              : "No courses found"}
          </p>
        )}

        {/* Load More */}
        {hasMore && courses.length > 0 && (
          <div className="text-center mt-10">
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="px-6 py-2 border border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Loading..." : "Load More"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
